import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IoIosArrowRoundForward } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';

import useGetUserTier from '@/services/user/queries/useGetUserTier';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import ROUTES from '@/routers/Routes';
import TierAvatarProgress from '@/features/avatar/TierAvatarProgress';
import UserAvatar from '@/features/avatar/Avatar';
import { ReducerState } from '@/services/common/lib/reducerState';
import { useWalletAddress } from '@/features/account-abstraction/hooks/useWalletAddress';
import useWalletBalance from '@/hooks/useWalletBalance';
import config from '@/common/config';
import { formatCurrency } from '@/pages/table/utils/utils';
import { ReactComponent as TetherIcon } from '@/assets/profile/tether.svg';

import TierIcon from './TierIcon';

const UserWalletMobileCard = () => {
  const user = useSelector((state: ReducerState) => state.auth);
  const navigate = useNavigate();

  const { walletAddress, isLoading: isLoadingWallet } = useWalletAddress();

  const { data: usdtBalance, isLoading: isLoadingBalance } = useWalletBalance(walletAddress, config.contracts.usdt);

  const handleClickArrow = () => navigate(ROUTES.MY_HUB);

  const { isLoading, isSuccess, data } = useGetUserTier({
    userId: user.id || '',
  });

  const usdtBalanceDisplay = useMemo(() => {
    if (!usdtBalance) return '-';

    return formatCurrency(Number(usdtBalance.displayBalance));
  }, [usdtBalance]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user || !user.id || !isSuccess || !data) {
    return null;
  }

  const userTier = data.data;

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-2">
        <TierAvatarProgress className="h-[52px] w-[52px]" small progressPercentage={userTier.tierProgressPercentage}>
          <UserAvatar userId={user.id} className="h-[40px] w-[40px]" />
        </TierAvatarProgress>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold leading-[22px] text-ph-gray-white">{user.alias}</h2>
            <TierIcon tier={userTier.currentTier?.tier} className="h-5 w-5" />
          </div>

          <div className="flex items-center gap-2">
            <TetherIcon className="h-5 w-5" />
            <p className="text-sm font-semibold text-ph-gray-white">
              {isLoadingWallet || isLoadingBalance ? <LoadingSpinner size="4" /> : usdtBalanceDisplay}
            </p>
          </div>
        </div>
      </div>
      <button
        className="flex h-[44px] w-[44px] items-center justify-center gap-8 rounded-[10px] bg-ph-violet-light"
        onClick={handleClickArrow}
      >
        <IoIosArrowRoundForward size={24} className="text-lg font-bold text-ph-gray-evening" />
      </button>
    </div>
  );
};

export default UserWalletMobileCard;
